<script setup>
import HelloWorld from '../components/HelloWorld.vue';
import { ref, reactive, computed, toRaw, onMounted, onUnmounted, watch } from 'vue';

import ElementCard from '@/components/ElementCard.vue';
import ConfirmDialog from 'primevue/confirmdialog';
import Drawer from 'primevue/drawer';
import draggable from 'vuedraggable';
import TemplateEditorDialog from '@/components/TemplateEditorDialog.vue';
import ProjectEditorDialog from '@/components/ProjectEditorDialog.vue';

// Config components
import TextControl from '@/components/controls/textControl.vue';
import ColorControl from '@/components/controls/colorControl.vue';
import SelectControl from '@/components/controls/selectControl.vue';

// Helpers
import { generateDataFromTemplate, generateGuid, getTemplateAutohide } from '@/helpers/templateHelper.js';

// Services
import { useConfirm } from "primevue/useconfirm";
import { CentrifugeClient } from '@/services/CentrifugeClient.js';

const confirm = useConfirm();

// Crear instancia del cliente de Centrifuge
let centrifugeClient = null;

// Arrays reactivos vacíos - se cargarán desde archivos JSON
const templates = reactive([]);

const templateElements = reactive([]);

const getTemplateById = function(id) {
  return templates.find(template => template.id === id);
}

const updateElementData = function(elemid, datavalue) {
  const element = templateElements.find(e => e.id === elemid);
  if(element != null) {
    element.data = datavalue;
  }
}

const updateElementConcreteData = function (elemid, propname, datavalue) {
  const element = templateElements.find(e => e.id === elemid);
  if (element != null) {
    element.data[propname] = datavalue;
    
    // Enviar update inmediatamente cuando cambia un dato
    sendElementUpdate(element);
  }
}

// Drawer mechanism
const drawerVisible = ref(false);
let selectedElement = reactive({ data: []});

const openDrawer = function(element) {
  selectedElement = reactive(element);
  drawerVisible.value = true;
}

// ANCHOR: Config components
const configComponents = {
  'text': TextControl,
  'color': ColorControl,
  'select': SelectControl
}

// Computed property with the template on selectedElement
const template = computed(() => {
  if (selectedElement) {
    return templates.find(template => template.id === selectedElement.item);
  }
  return null;
});

// Computed property of the selectedElement in templateElements
const selectedElementInTemplateElements = computed(() => {
  return templateElements.find(element => element.id === selectedElement.id);
});

// ANCHOR: Element modifications
const copyElement = function(element) {
  const newElement = JSON.parse(JSON.stringify(element));
  newElement.id = randguid();
  newElement.name = `(Copia) ${newElement.name}`;
  
  // Push the new element next to original
  const index = templateElements.findIndex(e => e.id === element.id);
  if (index !== -1) {
    templateElements.splice(index + 1, 0, newElement);
  } else {
    templateElements.push(newElement);
  }
};

const deleteElement = function (element) {
  confirm.require({
    message: '¿Quieres borrar el elemento ' + element.name + '?',
    header: 'Borrar elemento',
    icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Cancelar',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Borrar',
      severity: 'danger',
    },
    accept: () => {
      const index = templateElements.findIndex(e => e.id === element.id);
      if (index !== -1) {
        templateElements.splice(index, 1);
      }
    },
    reject: () => {

    }
  });
};

const handleDrag = (event) => {
  // vuedraggable ya maneja el reordenamiento automáticamente
  // No necesitamos hacer nada aquí, solo dejamos que se dispare el evento
  console.log('Elemento reordenado:', event);
};

// ANCHOR: Template management
const fileInput = ref(null);
const projectFileInput = ref(null);
const selectedTemplate = ref(null);
const editorDialogVisible = ref(false);
const projectEditorDialogVisible = ref(false);
const showQuickEditControls = ref(false); // Estado para mostrar/ocultar controles de edición rápida (por defecto oculto)

// Detectar si estamos en vista móvil
const isMobileView = ref(window.innerWidth < 768);

// Listener para cambios de tamaño de ventana
const updateMobileView = () => {
  isMobileView.value = window.innerWidth < 768;
};

// Estados de colapso para las columnas (solo móvil) - Por defecto colapsadas en móvil
const isPresentationCollapsed = ref(true);
const isPreviewCollapsed = ref(false); // Vista previa visible por defecto
const isProjectCollapsed = ref(true);
const isTemplatesCollapsed = ref(true);

const selectTemplate = function(template) {
  selectedTemplate.value = template;
};

// Funciones para mostrar/ocultar controles de edición rápida
const hideQuickEdit = function() {
  showQuickEditControls.value = false;
};

const showQuickEdit = function() {
  showQuickEditControls.value = true;
};

// Funciones para toggle de columnas
const togglePresentationCollapse = function() {
  isPresentationCollapsed.value = !isPresentationCollapsed.value;
};

const togglePreviewCollapse = function() {
  isPreviewCollapsed.value = !isPreviewCollapsed.value;
};

const toggleProjectCollapse = function() {
  isProjectCollapsed.value = !isProjectCollapsed.value;
};

const toggleTemplatesCollapse = function() {
  isTemplatesCollapsed.value = !isTemplatesCollapsed.value;
};

const openTemplateEditor = function(template) {
  selectedTemplate.value = template;
  editorDialogVisible.value = true;
};

const handleTemplateSave = function(editedData) {
  if (selectedTemplate.value) {
    selectedTemplate.value.content = editedData.content;
    selectedTemplate.value.script = editedData.script;
    selectedTemplate.value.controls = editedData.controls;
    
    console.log('✅ Plantilla actualizada:', selectedTemplate.value.name);
    
    // Enviar updatetemplate a todos los elementos que usan esta plantilla
    const affectedElements = templateElements.filter(el => el.item === selectedTemplate.value.id);
    console.log(`📤 Enviando updatetemplate a ${affectedElements.length} elementos asociados...`);
    
    affectedElements.forEach(element => {
      const template = getTemplateById(element.item);
      
      const message = {
        action: 'updatetemplate',
        item: element.item,
        elementId: element.id,
        data: element.data,
        content: template?.content || '',
        script: template?.script || ''
      };
      
      if (centrifugeClient) {
        centrifugeClient.publish(message);
      }
      console.log('🔄 Update template for element:', element.name);
    });
  }
};

const addElementFromTemplate = function(template) {
  if (!template) {
    console.warn('No hay plantilla para crear elemento');
    return;
  }
  
  // Generar el data a partir de los controls de la plantilla
  const data = generateDataFromTemplate(template);
  
  // Crear el nuevo elemento
  const newElement = {
    item: template.id,
    name: `${template.name} - Nuevo`,
    id: generateGuid(),
    autooff: getTemplateAutohide(template), // Usar autohide de la plantilla (0 si no existe)
    data: data
  };
  
  // Agregar al array de elementos
  templateElements.push(newElement);
  
  console.log('✅ Elemento creado:', newElement);
};

const loadTemplate = function() {
  fileInput.value.click();
};

const handleTemplateFile = function(event) {
  const file = event.target.files[0];
  console.log('Archivo seleccionado:', file);
  
  if (file) {
    const reader = new FileReader();
    reader.onload = function(e) {
      try {
        console.log('Contenido del archivo:', e.target.result);
        const templateData = JSON.parse(e.target.result);
        console.log('Template parseado:', templateData);
        
        // Validar que tenga la estructura correcta
        if (templateData.name && templateData.id && templateData.content && templateData.controls) {
          console.log('Estructura válida, verificando duplicados...');
          // Verificar que no exista ya una plantilla con el mismo ID
          const existingTemplate = templates.find(t => t.id === templateData.id);
          if (!existingTemplate) {
            templates.push(templateData);
            console.log('✅ Plantilla cargada exitosamente:', templateData.name);
            alert(`Plantilla "${templateData.name}" cargada exitosamente`);
          } else {
            console.warn('⚠️ Ya existe una plantilla con el ID:', templateData.id);
            alert(`Ya existe una plantilla con el ID: ${templateData.id}`);
          }
        } else {
          console.error('❌ Formato de plantilla inválido. Estructura requerida:', {
            name: templateData.name ? '✓' : '✗',
            id: templateData.id ? '✓' : '✗', 
            content: templateData.content ? '✓' : '✗',
            controls: templateData.controls ? '✓' : '✗'
          });
          alert('Formato de plantilla inválido. Revisa que tenga name, id, content y controls.');
        }
      } catch (error) {
        console.error('❌ Error al parsear el archivo JSON:', error);
        alert('Error al parsear el archivo JSON. Verifica que sea un JSON válido.');
      }
    };
    reader.readAsText(file);
  } else {
    console.warn('No se seleccionó ningún archivo');
  }
  // Limpiar el input
  event.target.value = '';
};

const saveSelectedTemplate = function() {
  if (!selectedTemplate.value) {
    console.warn('No hay plantilla seleccionada');
    return;
  }
  
  const dataStr = JSON.stringify(selectedTemplate.value, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${selectedTemplate.value.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`;
  link.click();
  URL.revokeObjectURL(url);
};

const deleteTemplate = function(template) {
  // Contar elementos asociados
  const associatedElements = templateElements.filter(el => el.item === template.id);
  const elementCount = associatedElements.length;
  
  confirm.require({
    message: `¿Quieres borrar la plantilla "${template.name}"?${elementCount > 0 ? ` Esto también borrará ${elementCount} elemento(s) asociado(s).` : ''}`,
    header: 'Borrar Plantilla',
    icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Cancelar',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Borrar',
      severity: 'danger',
    },
    accept: () => {
      // Borrar elementos asociados
      for (let i = templateElements.length - 1; i >= 0; i--) {
        if (templateElements[i].item === template.id) {
          templateElements.splice(i, 1);
        }
      }
      
      // Borrar plantilla
      const templateIndex = templates.findIndex(t => t.id === template.id);
      if (templateIndex !== -1) {
        templates.splice(templateIndex, 1);
      }
      
      // Limpiar selección si era la plantilla seleccionada
      if (selectedTemplate.value?.id === template.id) {
        selectedTemplate.value = null;
      }
      
      console.log('✅ Plantilla borrada:', template.name, `(${elementCount} elementos asociados borrados)`);
    },
    reject: () => {
      console.log('❌ Borrado de plantilla cancelado');
    }
  });
};

// ANCHOR: Presentation screen
// ID de presentación (genera uno nuevo si no existe)
const presentationScreenId = ref(generateGuid());

// URL base para la presentación
const presentationBaseUrl = computed(() => {
  return `${window.location.origin}/presentation/${presentationScreenId.value}`;
});

// Sincronizar cambios del presentationScreenId con el cliente
watch(presentationScreenId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    console.log(`⚡ Presentation ID changed: ${oldId} -> ${newId}`);
    
    // Si no hay cliente, crear uno nuevo
    if (!centrifugeClient) {
      console.log('🔌 Creando nuevo cliente Centrifuge...');
      centrifugeClient = new CentrifugeClient(newId);
      centrifugeClient.connect();
    } else {
      // Si existe, desconectar y crear uno nuevo con el nuevo ID
      console.log('🔄 Recreando cliente Centrifuge con nuevo ID...');
      centrifugeClient.disconnect();
      centrifugeClient = new CentrifugeClient(newId);
      centrifugeClient.connect();
    }
  }
});

const openPresentationInterface = function() {
  const url = `${window.location.origin}/presentation/${presentationScreenId.value}`;
  window.open(url, '_blank');
  console.log('Abriendo interfaz de presentación:', url);
};

const copyPresentationURL = function() {
  const url = `${window.location.origin}/presentation/${presentationScreenId.value}`;
  navigator.clipboard.writeText(url).then(() => {
    alert('URL copiada al portapapeles');
    console.log('URL copiada:', url);
  }).catch(err => {
    console.error('Error al copiar URL:', err);
    alert('Error al copiar la URL');
  });
};

const killAllElements = function() {
  const message = {
    action: 'killall'
  };
  
  if (centrifugeClient) {
    centrifugeClient.publish(message);
  }
  console.log('💀 Kill all elements sent');
};

const cleanAllElements = function() {
  const message = {
    action: 'clean'
  };
  
  if (centrifugeClient) {
    centrifugeClient.publish(message);
  }
  console.log('🧹 Clean all elements sent');
};

// ANCHOR: Project management
const deleteAllProject = function() {
  confirm.require({
    message: '¿Estás seguro de que quieres borrar TODAS las plantillas y elementos? Esta acción no se puede deshacer.',
    header: 'Borrar Todo el Proyecto',
    icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Cancelar',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Borrar Todo',
      severity: 'danger',
    },
    accept: () => {
      // Limpiar plantillas
      templates.splice(0, templates.length);
      // Limpiar elementos
      templateElements.splice(0, templateElements.length);
      // Limpiar selección
      selectedTemplate.value = null;
      // Regenerar ID de conexión (esto disparará el watch que recrea el cliente)
      presentationScreenId.value = generateGuid();
      
      console.log('✅ Proyecto limpiado completamente (incluyendo ID de conexión)');
      alert('Proyecto limpiado exitosamente');
    },
    reject: () => {
      console.log('❌ Operación cancelada');
    }
  });
};

const loadProject = function() {
  projectFileInput.value.click();
};

const handleProjectFile = function(event) {
  const file = event.target.files[0];
  console.log('Archivo de proyecto seleccionado:', file);
  
  if (file) {
    const reader = new FileReader();
    reader.onload = function(e) {
      try {
        console.log('Contenido del archivo:', e.target.result);
        const projectData = JSON.parse(e.target.result);
        console.log('Proyecto parseado:', projectData);
        
        // Validar que tenga la estructura correcta
        if (projectData.templates && projectData.elements && 
            Array.isArray(projectData.templates) && Array.isArray(projectData.elements)) {
          
          // PASO 1: Ejecutar la misma rutina que "Borrar Todo"
          // Limpiar plantillas
          templates.splice(0, templates.length);
          // Limpiar elementos
          templateElements.splice(0, templateElements.length);
          // Limpiar selección
          selectedTemplate.value = null;
          
          // PASO 2: Cargar ID de presentación del proyecto (esto disparará el watch que recrea el cliente)
          if (projectData.presentationScreenId) {
            presentationScreenId.value = projectData.presentationScreenId;
          } else {
            presentationScreenId.value = generateGuid();
          }
          
          // PASO 3: Cargar nuevas plantillas
          projectData.templates.forEach(template => {
            templates.push(template);
          });
          
          // PASO 4: Cargar nuevos elementos
          projectData.elements.forEach(element => {
            templateElements.push(element);
          });
          
          console.log('✅ Proyecto cargado exitosamente:', {
            templates: projectData.templates.length,
            elements: projectData.elements.length,
            presentationScreenId: presentationScreenId.value
          });
          
          alert(`Proyecto cargado: ${projectData.templates.length} plantillas y ${projectData.elements.length} elementos`);
        } else {
          console.error('❌ Formato de proyecto inválido. Estructura requerida:', {
            templates: projectData.templates && Array.isArray(projectData.templates) ? '✓' : '✗',
            elements: projectData.elements && Array.isArray(projectData.elements) ? '✓' : '✗'
          });
          alert('Formato de proyecto inválido. Debe contener arrays "templates" y "elements".');
        }
      } catch (error) {
        console.error('❌ Error al parsear el archivo JSON:', error);
        alert('Error al parsear el archivo JSON. Verifica que sea un JSON válido.');
      }
    };
    reader.readAsText(file);
  } else {
    console.warn('No se seleccionó ningún archivo');
  }
  // Limpiar el input
  event.target.value = '';
};

const saveProject = function() {
  const projectData = {
    version: "1.0",
    timestamp: new Date().toISOString(),
    presentationScreenId: presentationScreenId.value,
    templates: templates,
    elements: templateElements
  };
  
  const dataStr = JSON.stringify(projectData, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  
  // Nombre del archivo con fecha
  const date = new Date().toISOString().split('T')[0];
  link.download = `suricatta_project_${date}.json`;
  
  link.click();
  URL.revokeObjectURL(url);
  
  console.log('✅ Proyecto guardado:', {
    templates: templates.length,
    elements: templateElements.length
  });
};

// Computed property para obtener el proyecto actual
const currentProject = computed(() => ({
  version: "1.0",
  timestamp: new Date().toISOString(),
  presentationScreenId: presentationScreenId.value,
  templates: templates,
  elements: templateElements
}));

// Función para abrir el editor de proyecto
const openProjectEditor = function() {
  projectEditorDialogVisible.value = true;
};

// Función para guardar cambios desde el editor de proyecto
const handleProjectSave = function(editedProject) {
  // PASO 1: Ejecutar la misma rutina que "Borrar Todo"
  // Limpiar plantillas
  templates.splice(0, templates.length);
  // Limpiar elementos
  templateElements.splice(0, templateElements.length);
  // Limpiar selección
  selectedTemplate.value = null;
  
  // PASO 2: Actualizar ID de presentación (esto disparará el watch que recrea el cliente)
  if (editedProject.presentationScreenId) {
    presentationScreenId.value = editedProject.presentationScreenId;
  }
  
  // PASO 3: Cargar nuevas plantillas
  editedProject.templates.forEach(template => {
    templates.push(template);
  });
  
  // PASO 4: Cargar nuevos elementos
  editedProject.elements.forEach(element => {
    templateElements.push(element);
  });
  
  console.log('✅ Proyecto actualizado desde el editor:', {
    templates: templates.length,
    elements: templateElements.length
  });
};

// ANCHOR: Play/Stop handlers
// Mapa para almacenar los timers activos por elemento
const activeTimers = reactive({});
const timerCountdowns = reactive({});

const handlePlay = function(element) {
  const template = getTemplateById(element.item);
  
  const message = {
    action: 'play',
    item: element.item,
    elementId: element.id,
    data: element.data,
    content: template?.content || '',
    script: template?.script || ''
  };
  
  if (centrifugeClient) {
    centrifugeClient.publish(message);
  }
  console.log('▶️ Play element:', element.name, message);
  
  // Si tiene auto-off activado y segundos > 0
  if (element.autooff > 0) {
    // Limpiar timer anterior si existe
    if (activeTimers[element.id]) {
      clearInterval(activeTimers[element.id]);
    }
    
    // Iniciar countdown
    timerCountdowns[element.id] = element.autooff;
    
    // Crear intervalo para countdown
    activeTimers[element.id] = setInterval(() => {
      timerCountdowns[element.id]--;
      
      if (timerCountdowns[element.id] <= 0) {
        // Enviar stop automático
        handleStop(element);
        // Limpiar timer
        clearInterval(activeTimers[element.id]);
        delete activeTimers[element.id];
        delete timerCountdowns[element.id];
      }
    }, 1000);
    
    console.log(`⏱️ Auto-off programado para ${element.autooff} segundos`);
  }
};

const handleStop = function(element) {
  const template = getTemplateById(element.item);
  
  const message = {
    action: 'stop',
    item: element.item,
    elementId: element.id,
    data: element.data,
    content: template?.content || '',
    script: template?.script || ''
  };
  
  if (centrifugeClient) {
    centrifugeClient.publish(message);
  }
  console.log('⏹️ Stop element:', element.name, message);
  
  // Limpiar timer si existe
  if (activeTimers[element.id]) {
    clearInterval(activeTimers[element.id]);
    delete activeTimers[element.id];
    delete timerCountdowns[element.id];
    console.log('⏱️ Timer cancelado');
  }
};

// Función para enviar update cuando cambian los datos de un elemento
const sendElementUpdate = function(element) {
  const template = getTemplateById(element.item);
  
  const message = {
    action: 'update',
    item: element.item,
    elementId: element.id,
    data: element.data,
    content: template?.content || '',
    script: template?.script || ''
  };
  
  if (centrifugeClient) {
    centrifugeClient.publish(message);
  }
  console.log('🔄 Update element:', element.name, message);
};

// ANCHOR: Event bus
let randguid = generateGuid;

// Lifecycle hooks
onMounted(() => {
  // Crear y conectar el cliente de Centrifuge
  centrifugeClient = new CentrifugeClient(presentationScreenId.value);
  centrifugeClient.connect();
  
  // Añadir listener para cambios de tamaño de ventana
  window.addEventListener('resize', updateMobileView);
  
  console.log('HomeView mounted - Channel ID:', presentationScreenId.value);
});

onUnmounted(() => {
  // Desconectar y limpiar el cliente principal
  if (centrifugeClient) {
    centrifugeClient.disconnect();
    centrifugeClient = null;
  }
  
  // Remover listener de resize
  window.removeEventListener('resize', updateMobileView);
  
  console.log('HomeView unmounted - Disconnected from Centrifuge');
});
</script>

<template>
  <!-- Split template -->
  <div class="main-container">
    <div class="w-full first-block">
        <div class="columns-container">
          <!-- Columna 1: Pantalla de presentación (1/4) -->
          <div class="column-item bg-orange-100 border-round p-2 flex flex-column">
            <div class="flex justify-content-between align-items-center mb-1">
              <h4 class="mt-0 mb-0 text-sm font-semibold">Pantalla de presentación</h4>
              <button 
                @click="togglePresentationCollapse"
                class="collapse-toggle md:hidden p-1 border-round cursor-pointer"
                style="background: rgba(0,0,0,0.1); border: none; font-size: 1rem; line-height: 1; width: 24px; height: 24px;"
              >
                {{ isPresentationCollapsed ? '▼' : '▲' }}
              </button>
            </div>
            <div v-if="!isMobileView || !isPresentationCollapsed" class="flex flex-column gap-1 flex-1">
              <div>
                <label for="presentationId" class="text-xs block mb-1">ID de conexión</label>
                <input 
                  id="presentationId"
                  v-model="presentationScreenId" 
                  type="text" 
                  class="w-full text-xs p-1 border-1 surface-border border-round"
                  placeholder="ID de conexión"
                />
              </div>
              <div class="flex gap-1">
                <button 
                  @click="openPresentationInterface"
                  class="flex-1 text-xs p-1 border-round cursor-pointer"
                  style="background: #e5e7eb; border: 1px solid #d1d5db;"
                >
                  Abrir interfaz
                </button>
                <button 
                  @click="copyPresentationURL"
                  class="flex-1 text-xs p-1 border-round cursor-pointer"
                  style="background: #e5e7eb; border: 1px solid #d1d5db;"
                >
                  Copiar URL
                </button>
              </div>

            </div>
          </div>

          <!-- Columna 2: Vista Previa (1/4) -->
          <div class="column-item bg-purple-100 border-round p-2 flex flex-column">
            <div class="flex justify-content-between align-items-center mb-1">
              <h4 class="mt-0 mb-0 text-sm font-semibold">Vista Previa</h4>
              <button 
                @click="togglePreviewCollapse"
                class="collapse-toggle md:hidden p-1 border-round cursor-pointer"
                style="background: rgba(0,0,0,0.1); border: none; font-size: 1rem; line-height: 1; width: 24px; height: 24px;"
              >
                {{ isPreviewCollapsed ? '▼' : '▲' }}
              </button>
            </div>
            <div :class="['flex-1 flex flex-column', {'preview-hidden': isMobileView && isPreviewCollapsed}]" style="min-height: 0;">
              <iframe 
                :src="presentationBaseUrl"
                class="preview-iframe"
                frameborder="0"
                style="width: 100%; height: 100%; border-radius: 4px; background: #000;"
              ></iframe>
            </div>
          </div>

          <!-- Columna 3: Proyecto (1/4) -->
          <div class="column-item bg-green-100 border-round p-2 flex flex-column">
            <div class="flex justify-content-between align-items-center mb-1">
              <h4 class="mt-0 mb-0 text-sm font-semibold">Proyecto</h4>
              <button 
                @click="toggleProjectCollapse"
                class="collapse-toggle md:hidden p-1 border-round cursor-pointer"
                style="background: rgba(0,0,0,0.1); border: none; font-size: 1rem; line-height: 1; width: 24px; height: 24px;"
              >
                {{ isProjectCollapsed ? '▼' : '▲' }}
              </button>
            </div>
            <div v-if="!isMobileView || !isProjectCollapsed" class="flex-1 flex flex-column justify-content-center gap-2">
              <!-- Botón Editar Proyecto arriba -->
              <button @click="openProjectEditor"
                      class="p-2 bg-purple-500 text-white border-round border-none cursor-pointer text-xs hover:bg-purple-600 transition-colors font-semibold">
                ✏️ Editar Proyecto
              </button>
              
              <!-- Botones Cargar y Guardar en paralelo -->
              <div class="flex gap-2">
                <button @click="loadProject"
                        class="flex-1 p-2 bg-primary text-white border-round border-none cursor-pointer text-xs hover:bg-primary-600 transition-colors font-semibold">
                  📂 Cargar
                </button>
                <button @click="saveProject"
                        class="flex-1 p-2 bg-green-500 text-white border-round border-none cursor-pointer text-xs hover:bg-green-600 transition-colors font-semibold">
                  💾 Guardar
                </button>
              </div>
              
              <!-- Botón Borrar Todo abajo -->
              <button @click="deleteAllProject"
                      class="p-2 bg-red-500 text-white border-round border-none cursor-pointer text-xs hover:bg-red-600 transition-colors font-semibold">
                🗑️ Borrar Todo
              </button>
              
              <input ref="projectFileInput" 
                     type="file" 
                     accept=".json" 
                     @change="handleProjectFile" 
                     style="display: none;" />
            </div>
          </div>

          <!-- Columna 4: Plantillas (1/4) -->
          <div class="column-item bg-blue-100 border-round p-2 flex flex-column">
            <div class="flex justify-content-between align-items-center mb-1">
              <h4 class="mt-0 mb-0 text-sm font-semibold">Plantillas</h4>
              <button 
                @click="toggleTemplatesCollapse"
                class="collapse-toggle md:hidden p-1 border-round cursor-pointer"
                style="background: rgba(0,0,0,0.1); border: none; font-size: 1rem; line-height: 1; width: 24px; height: 24px;"
              >
                {{ isTemplatesCollapsed ? '▼' : '▲' }}
              </button>
            </div>
            <div v-if="!isMobileView || !isTemplatesCollapsed" class="flex-1 overflow-auto mb-2">
              <div v-for="template in templates" :key="template.id"
                :class="['mb-1 px-2 py-1 border-round text-xs transition-colors leading-tight flex justify-content-between align-items-center', 
                           selectedTemplate?.id === template.id ? 'bg-primary text-white' : 'bg-white hover:bg-gray-100']">
                <div class="flex-1 cursor-pointer" @click="selectTemplate(template)">
                  <div class="font-semibold leading-tight line-height-1">{{ template.name }}</div>
                  <div class="text-xs opacity-70 leading-tight line-height-1">
                    ID: {{ template.id }}
                  </div>
                </div>
                <div class="flex gap-1 ml-2">
                  <button @click.stop="addElementFromTemplate(template)" 
                          :class="['p-1 border-round border-none cursor-pointer text-xs transition-colors',
                                  selectedTemplate?.id === template.id ? 'bg-white text-primary hover:bg-gray-100' : 'bg-primary text-white hover:bg-primary-600']"
                          title="Agregar elemento">
                    ➕
                  </button>
                  <button @click.stop="openTemplateEditor(template)"
                          :class="['p-1 border-round border-none cursor-pointer text-xs transition-colors',
                                  selectedTemplate?.id === template.id ? 'bg-white text-primary hover:bg-gray-100' : 'bg-green-500 text-white hover:bg-green-600']"
                          title="Desarrollo">
                    🔧
                  </button>
                  <button @click.stop="deleteTemplate(template)" 
                          :class="['p-1 border-round border-none cursor-pointer text-xs transition-colors',
                                  selectedTemplate?.id === template.id ? 'bg-white text-red-600 hover:bg-red-50' : 'bg-red-500 text-white hover:bg-red-600']"
                          title="Borrar plantilla">
                    🗑️
                  </button>
                </div>
              </div>
            </div>
            <div v-if="!isMobileView || !isTemplatesCollapsed" class="flex gap-1">
              <button @click="loadTemplate"
                      class="flex-1 p-1 bg-primary text-white border-round border-none cursor-pointer text-xs hover:bg-primary-600 transition-colors">
                📁 Cargar
              </button>
              <button @click="saveSelectedTemplate" 
                      :disabled="!selectedTemplate"
                      :class="['flex-1 p-1 border-round border-none cursor-pointer text-xs transition-colors',
                              selectedTemplate ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                💾 Guardar
              </button>
              <input ref="fileInput" 
                     type="file" 
                     accept=".json" 
                     @change="handleTemplateFile" 
                     style="display: none;" />
            </div>
          </div>
        </div>
      </div>
      
      <!-- Barra de acciones fija -->
      <div class="actions-bar">
        <div class="actions-left">
          <button 
            @click="killAllElements"
            class="action-button action-button-kill"
            title="Ejecuta stop() en todos los elementos activos"
          >
            💀 Matar
          </button>
          <button 
            @click="cleanAllElements"
            class="action-button action-button-clean"
            title="Elimina todos los elementos de la pantalla"
          >
            🧹 Cancelar
          </button>
        </div>
        <div class="actions-right">
          <button 
            v-if="showQuickEditControls"
            @click="hideQuickEdit"
            class="action-button action-button-hide"
            title="Oculta los controles de edición rápida de todas las tarjetas"
          >
            👁️ Ocultar
          </button>
          <button 
            v-if="!showQuickEditControls"
            @click="showQuickEdit"
            class="action-button action-button-show"
            title="Muestra los controles de edición rápida de todas las tarjetas"
          >
            👁️ Mostrar
          </button>
        </div>
      </div>

      <div class="second-block">
        <draggable :list="templateElements" @change="handleDrag" itemKey="id" handle=".drag-from-here"
          class="cards-container flex flex-row flex-wrap" style="width: 100%; margin: 0;">
          <template #item="{ element, index }">
            <div :key="element.id" class="card-wrapper">
              <ElementCard :element="element" @update:element="(val) => updateElementData(element.id, val)"
                :template="getTemplateById(element.item)" @requestConfig="openDrawer"
                :configComponents="configComponents" @requestCopy="el => copyElement(el)"
                @requestDelete="el => deleteElement(el)"
                @requestPlay="el => handlePlay(el)"
                @requestStop="el => handleStop(el)"
                @updateData="updateElementConcreteData"
                :countdown="timerCountdowns[element.id]"
                :showQuickEdit="showQuickEditControls" />
            </div>
          </template>
        </draggable>
      </div>

    <!-- Drawer -->
    <Drawer v-model:visible="drawerVisible" header="Configuración" position="right">
      <div class="text-sm line-height-2 mb-3">
        Configuración de plantilla <span class="font-bold">{{ template.name }}</span>
      </div>

      <!-- Nombre del elemento -->
      <div class="mb-2">
        <label class="text-sm font-semibold mb-1 block">Nombre del elemento</label>
        <input 
          v-model="selectedElement.name" 
          type="text"
          class="w-full p-2 border-round border-1 border-gray-300 text-sm"
          placeholder="Nombre del elemento"
        />
      </div>

      <div>
        <div v-for="section in template.controls.sections" :key="section.id">
          <div class="pt-1 pb-1 border-gray-300 border-bottom-2"></div>
          <div class="text-lg font-bold mt-1 mb-0 pb-0 line-height-2">
            {{ section.name }}
          </div>
          <div class="text-sm mt-0 mb-2 line-height-2">
            {{ section.description }}
          </div>
          <div v-for="item in section.items" :key="item.id">
            <component :is="configComponents[item.type]"
              :content="selectedElement?.data[item.id] !== undefined ? selectedElement.data[item.id] : item.default"
              @update:content="updateElementConcreteData(selectedElement.id, item.id, $event)"
              :validation="item.validation" :element="item" />
          </div>
        </div>
      </div>
    </Drawer>

    <!-- Confirm dialog -->
    <ConfirmDialog></ConfirmDialog>

    <!-- Template Editor Dialog -->
    <TemplateEditorDialog 
      v-model:visible="editorDialogVisible"
      :template="selectedTemplate"
      @save="handleTemplateSave"
    />

    <!-- Project Editor Dialog -->
    <ProjectEditorDialog 
      v-model:visible="projectEditorDialogVisible"
      :projectData="currentProject"
      @save="handleProjectSave"
    />
  </div>
</template>

<style>
/* Contenedor principal - En móvil fluye, en desktop tiene altura fija */
.main-container {
  display: flex;
  flex-direction: column;
}

@media (min-width: 768px) {
  .main-container {
    height: 100vh;
    overflow: hidden;
  }
}

/* Primera sección (columnas superiores) - En móvil sin restricciones, en desktop con altura fija */
.first-block {
  width: 100%;
}

@media (min-width: 768px) {
  .first-block {
    flex-shrink: 0;
    overflow-x: auto;
    height: 16rem; /* Aumentado de 12rem a 16rem para ver mejor el iframe */
  }
}

/* Contenedor de columnas - En móvil apiladas verticalmente, en desktop horizontales */
.columns-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem;
}

@media (min-width: 768px) {
  .columns-container {
    flex-direction: row;
    height: 100%;
    gap: 0.5rem;
  }
}

/* Columnas individuales - En móvil ancho completo, en desktop flex:1 */
.column-item {
  width: 100%;
  min-width: 280px;
  transition: all 0.3s ease;
}

@media (min-width: 768px) {
  .column-item {
    flex: 1;
    min-width: 0; /* Permite que flex ajuste correctamente */
    width: auto;
  }
}

/* Botón de colapso - Solo visible en móvil */
.collapse-toggle {
  transition: transform 0.3s ease;
}

.collapse-toggle:hover {
  background: rgba(0,0,0,0.2) !important;
}

/* Barra de acciones fija - sticky en móvil y desktop, ahora fuera del second-block */
.actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  background-color: #f3f4f6;
  border-bottom: 1px solid #d1d5db;
  border-top: 1px solid #d1d5db;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

/* Segunda sección (tarjetas) - En móvil fluye, en desktop ocupa el espacio restante */
.second-block {
  box-sizing: border-box;
  background-color: #e5e7eb;
  padding: 0;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

@media (min-width: 768px) {
  .second-block {
    flex: 1;
    overflow-y: hidden;
  }
}

.actions-left,
.actions-right {
  display: flex;
  gap: 0.25rem;
}

.action-button {
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.75rem;
  color: white;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.action-button-kill {
  background: #ef4444;
}

.action-button-kill:hover {
  background: #dc2626;
}

.action-button-clean {
  background: #f59e0b;
}

.action-button-clean:hover {
  background: #d97706;
}

.action-button-hide {
  background: #8b5cf6;
}

.action-button-hide:hover {
  background: #7c3aed;
}

.action-button-show {
  background: #06b6d4;
}

.action-button-show:hover {
  background: #0891b2;
}

/* Contenedor de tarjetas con scroll */
.cards-container {
  flex: 1;
  overflow-y: auto;
  padding: 0.25rem;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
}

.card-wrapper {
  width: 100%;
  padding: 0.25rem;
  box-sizing: border-box;
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .card-wrapper {
    width: 50%;
  }
}

@media (min-width: 768px) {
  .card-wrapper {
    width: 33.333333%;
  }
}

@media (min-width: 1024px) {
  .card-wrapper {
    width: 33.333333%;
  }
}

@media (min-width: 1280px) {
  .card-wrapper {
    width: 25%;
  }
}

/* Vista previa iframe */
.preview-iframe {
  pointer-events: none; /* Evita interacción accidental */
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Clase para ocultar vista previa sin desmontar el iframe */
.preview-hidden {
  visibility: hidden !important;
  position: absolute !important;
  height: 0 !important;
  overflow: hidden !important;
  pointer-events: none !important;
}
</style>
